import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import { JSX, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { parseApiError } from '../../../library/apiErrorHelper';
import { IInvoice, isWithinInvoiceWindow } from '../../../library/models/invoice';
import { IPatientPurchase } from '../../../library/models/patientPurchase';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactMutation } from '../../../services/queryService';
import { services } from '../../../services/services';
import { ConfirmationDialog } from '../../confirmationDialog/confirmationDialog';
import { useSnackbar } from '../../layout/snackbarProvider';
import { EmitInvoiceDialog } from './EmitInvoiceDialog';
import {
    errorTextStyles,
    hintStyles,
    invoiceRowStyles,
    receiptImageStyles,
} from './purchaseInvoiceActions.styles';

interface PurchaseInvoiceActionsProps {
    purchase: IPatientPurchase;
    /** The purchase's live invoice, if it has one. Cancelled ones are history, not this. */
    invoice?: IInvoice;
    patientName: string;
    patientEmail?: string;
    /** The patient's invoice list has to pick up whatever happens here. */
    onChanged: () => void;
}

/**
 * Whether a sale has been invoiced, and what can be done about it.
 *
 * Five states, and each of them is something a therapist standing at a desk needs to be able
 * to tell apart at a glance:
 *
 * - **not invoiced, inside the month** -- the button
 * - **not invoiced, past the month** -- no button, and a line saying why, because a missing
 *   button with no explanation reads as a bug
 * - **issued** -- the document number, linking to the PDF at the provider
 * - **failed** -- the provider's own words and a retry, because "seria FCT nu exista" is
 *   something the therapist can fix and an administrator would only have to ask them about
 * - **cancelled** -- nothing here at all: the invoice is history and the sale is invoiceable
 *   again, so this component is showing the "not invoiced" case
 *
 * Cancelling is administrators only, and the API refuses it either way -- withdrawing a fiscal
 * document is not a thing to be able to do by pressing the wrong button twice.
 */
export const PurchaseInvoiceActions = (props: PurchaseInvoiceActionsProps): JSX.Element | null => {
    const { t: translate } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const { role } = useAuth();

    const [isEmitting, setIsEmitting] = useState<boolean>(false);
    const [isConfirmingCancel, setIsConfirmingCancel] = useState<boolean>(false);
    const [receiptUrl, setReceiptUrl] = useState<string | null>(null);

    const retryInvoice = useReactMutation(services.invoicing.retryInvoiceMutation());
    const cancelInvoice = useReactMutation(services.invoicing.cancelInvoiceMutation());

    // The receipt preview is an object URL, so it has to be handed back or every peek leaks
    // a few hundred kilobytes for the life of the tab.
    useEffect(
        () => () => {
            if (receiptUrl) {
                URL.revokeObjectURL(receiptUrl);
            }
        },
        [receiptUrl]
    );

    const invoice: IInvoice | undefined = props.invoice;
    const isInvoiceable: boolean = props.purchase.status !== 'CANCELLED';

    // A patient's own view of their purchase has no price -- the API leaves money out for
    // that caller -- and there is nothing to invoice without one.
    const canEmit: boolean =
        !invoice && isInvoiceable && props.purchase.price !== null && isWithinInvoiceWindow(props.purchase);

    const isTooLate: boolean =
        !invoice && isInvoiceable && props.purchase.price !== null && !isWithinInvoiceWindow(props.purchase);

    const onShowReceipt = async (): Promise<void> => {
        if (!invoice) {
            return;
        }

        try {
            setReceiptUrl(await services.invoicing.fetchReceiptObjectUrl(invoice.id));
        } catch (error: unknown) {
            showSnackbar(
                parseApiError(error).message ?? translate(ResourcesIds.INVOICE_RECEIPT_ERROR),
                'error'
            );
        }
    };

    const onCloseReceipt = (): void => {
        if (receiptUrl) {
            URL.revokeObjectURL(receiptUrl);
        }

        setReceiptUrl(null);
    };

    const onRetry = (): void => {
        if (!invoice) {
            return;
        }

        retryInvoice.mutate(invoice.id, {
            onSuccess: (issued: IInvoice) => {
                showSnackbar(
                    translate(ResourcesIds.INVOICE_RETRY_SUCCESS, {
                        document: issued.documentNumber ?? '',
                    }),
                    'success'
                );
                props.onChanged();
            },
            onError: (error: unknown) =>
                showSnackbar(
                    parseApiError(error).message ?? translate(ResourcesIds.INVOICE_RETRY_ERROR),
                    'error'
                ),
        });
    };

    const onCancel = (): void => {
        if (!invoice) {
            return;
        }

        setIsConfirmingCancel(false);

        cancelInvoice.mutate(invoice.id, {
            onSuccess: () => {
                showSnackbar(translate(ResourcesIds.INVOICE_CANCEL_SUCCESS), 'success');
                props.onChanged();
            },
            onError: (error: unknown) =>
                showSnackbar(
                    parseApiError(error).message ?? translate(ResourcesIds.INVOICE_CANCEL_ERROR),
                    'error'
                ),
        });
    };

    // A cancelled purchase cannot be invoiced and never had to be, so the row says nothing
    // at all rather than explaining an absence nobody was wondering about.
    if (!invoice && !isInvoiceable) {
        return null;
    }

    return (
        <>
            <Box sx={invoiceRowStyles}>
                {canEmit && (
                    <Button variant="outlined" size="small" onClick={() => setIsEmitting(true)}>
                        {translate(ResourcesIds.EMIT_INVOICE_ACTION)}
                    </Button>
                )}

                {isTooLate && (
                    <Typography variant="caption" sx={hintStyles}>
                        {translate(ResourcesIds.INVOICE_WINDOW_EXPIRED)}
                    </Typography>
                )}

                {invoice?.status === 'ISSUED' && (
                    <>
                        <Chip
                            size="small"
                            color="success"
                            variant="outlined"
                            label={invoice.documentNumber ?? translate(ResourcesIds.INVOICE_STATUS_ISSUED)}
                        />
                        {invoice.documentUrl && (
                            <Button
                                size="small"
                                href={invoice.documentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {translate(ResourcesIds.INVOICE_VIEW_DOCUMENT)}
                            </Button>
                        )}
                        <Button size="small" onClick={onShowReceipt}>
                            {translate(ResourcesIds.INVOICE_VIEW_RECEIPT)}
                        </Button>
                        {role === 'ADMIN' && (
                            <Button
                                size="small"
                                color="error"
                                onClick={() => setIsConfirmingCancel(true)}
                                disabled={cancelInvoice.isPending}
                            >
                                {translate(ResourcesIds.INVOICE_CANCEL)}
                            </Button>
                        )}
                    </>
                )}

                {invoice?.status === 'PENDING' && (
                    <Chip
                        size="small"
                        color="default"
                        variant="outlined"
                        label={translate(ResourcesIds.INVOICE_STATUS_PENDING)}
                    />
                )}

                {invoice?.status === 'FAILED' && (
                    <>
                        <Chip
                            size="small"
                            color="error"
                            variant="outlined"
                            label={translate(ResourcesIds.INVOICE_STATUS_FAILED)}
                        />
                        <Button size="small" onClick={onRetry} disabled={retryInvoice.isPending}>
                            {translate(ResourcesIds.INVOICE_RETRY)}
                        </Button>
                        <Button size="small" onClick={onShowReceipt}>
                            {translate(ResourcesIds.INVOICE_VIEW_RECEIPT)}
                        </Button>
                        {invoice.lastError && (
                            <Typography variant="caption" sx={errorTextStyles}>
                                {invoice.lastError}
                            </Typography>
                        )}
                    </>
                )}
            </Box>

            {isEmitting && (
                <EmitInvoiceDialog
                    open={isEmitting}
                    purchase={props.purchase}
                    patientName={props.patientName}
                    patientEmail={props.patientEmail}
                    onClose={() => setIsEmitting(false)}
                    onEmitted={props.onChanged}
                />
            )}

            {/* Windowed rather than full-screen even on a phone: this is a glance at a
                photograph, not a place the therapist has moved to. */}
            <Dialog open={!!receiptUrl} onClose={onCloseReceipt} maxWidth="sm" fullWidth>
                <DialogTitle>{translate(ResourcesIds.INVOICE_VIEW_RECEIPT)}</DialogTitle>
                <DialogContent>
                    {receiptUrl && (
                        <Box
                            component="img"
                            src={receiptUrl}
                            alt={translate(ResourcesIds.EMIT_INVOICE_RECEIPT_PHOTO_ALT)}
                            sx={receiptImageStyles}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <ConfirmationDialog
                isOpen={isConfirmingCancel}
                title={translate(ResourcesIds.INVOICE_CANCEL_CONFIRM_TITLE)}
                description={translate(ResourcesIds.INVOICE_CANCEL_CONFIRM_MESSAGE, {
                    document: invoice?.documentNumber ?? '',
                })}
                confirmMessage={translate(ResourcesIds.INVOICE_CANCEL)}
                onConfirm={onCancel}
                onClose={() => setIsConfirmingCancel(false)}
            />
        </>
    );
};
